import CasinoRoundedIcon from '@mui/icons-material/CasinoRounded';
import { Fab } from '@mui/material';
import React from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { RootState } from '../../store';
import { drawCards, newReadingId, randomInt, Reading } from '../draw';
import { commitReading, sealDaily } from '../tarotSlice';

/** True in `npm start` and in an Android debug build (MainActivity exposes it as window.AndroidBuild). */
export const isDevMode = (): boolean => {
    if (process.env.NODE_ENV !== 'production') return true;
    try {
        return Boolean(
            (
                window as unknown as { AndroidBuild?: { isDebug(): boolean } }
            ).AndroidBuild?.isDebug()
        );
    } catch {
        return false;
    }
};

/**
 * Dev only: draws a new card of the day on the spot, replacing today's, with no ritual, video or ad.
 * The reading is saved to the history like a normal one (delete it from there if it gets in the way).
 */
export const DevDrawButton: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const name = useSelector((s: RootState) => s.tarot.profile.name);
    if (!isDevMode()) return null;

    const draw = () => {
        const reading: Reading = {
            id: newReadingId(),
            spread: 'daily',
            at: Date.now(),
            name: name || undefined,
            cards: drawCards(1),
            unlocked: true,
            seed: randomInt(1000),
        };
        dispatch(sealDaily(reading));
        dispatch(commitReading(reading));
        navigate(`/reading/${reading.id}`);
    };

    // Portalled to <body>: the home screen is its own stacking layer, under the footer ad bar.
    return createPortal(
        <Fab
            size="small"
            color="secondary"
            aria-label="Dev: draw a new card of the day"
            title="Dev: draw a new card of the day"
            onClick={draw}
            sx={{ position: 'fixed', right: 16, bottom: 16, zIndex: 2000 }}
        >
            <CasinoRoundedIcon />
        </Fab>,
        document.body
    );
};
